'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { 
  TagIcon,
  LinkIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
  ClipboardIcon
} from '@heroicons/react/24/outline';
import { Asset, getAssetIcon } from './AssetItem';
import { createAuthenticatedBlobUrl, revokeBlobUrls } from '@/app/api/proxy';
import TaggedSelect, { TaggedSelectOption } from '@/components/common/TaggedSelect';
import { getPublicAssetUrl } from '@/utils/assetUrl';

interface AssetDetailsProps {
  asset: Asset | null;
  isEditing: boolean;
  onStartEditing: () => void;
  onSaveEdits: (updatedAsset: Asset) => void;
  onCancelEditing: () => void;
  onDeleteAsset: (assetId: string) => void;
  onAddTag: (tags: string[]) => Promise<void>;
  onRemoveTag: (tagToRemove: string) => Promise<void>;
  availableTags: TaggedSelectOption<string>[];
  onPreview?: () => void;
}

export default function AssetDetails({
  asset,
  isEditing,
  onStartEditing,
  onSaveEdits,
  onCancelEditing,
  onDeleteAsset,
  onAddTag,
  onRemoveTag,
  availableTags,
  onPreview
}: AssetDetailsProps) {
  const [newTag, setNewTag] = useState('');
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [tagsToAdd, setTagsToAdd] = useState<TaggedSelectOption<string>[]>([]);
  const [copySuccess, setCopySuccess] = useState(false);

  const publicUrl = useMemo(() => {
    if (!asset?.id || !asset.id.startsWith('ast_')) return null;
    
    try {
      return getPublicAssetUrl(asset.id, asset.contentType || 'image');
    } catch (err) {
      console.error('Failed to generate public URL:', err);
      return null;
    }
  }, [asset]);
  
  // No longer need to initialize edited asset as we're using a dialog
  
  // Load authenticated media when asset changes
  useEffect(() => {
    let isMounted = true;

    const loadMedia = async () => {
      if (asset && (asset.contentType === 'image' || asset.contentType === 'video')) {
        try {
          // For URL-type assets, use the URL directly from meta.url
          if (asset.sourceType === 'url' && asset.originalAsset?.meta?.url) {
            if (isMounted) {
              setMediaUrl(asset.originalAsset.meta.url);
            }
          } else {
            // For file-type assets, always bypass cache to get fresh content
            // The proxy will use ?t=timestamp for cache busting
            // And key={mediaUrl} will force React to remount when URL changes
            const blobUrl = await createAuthenticatedBlobUrl(asset.id, true);
            if (isMounted) {
              setMediaUrl(blobUrl);
            }
          }
        } catch (error) {
          console.error('Error loading media:', error);
        }
      } else {
        setMediaUrl(null);
      }
    };

    loadMedia();
    
    // Only set isMounted to false, don't revoke URLs here
    // This prevents the URL from being revoked while still in the cache
    return () => {
      isMounted = false;
    };
  }, [asset]);  
  
  // Cleanup all blob URLs when component unmounts
  useEffect(() => {
    return () => {
      revokeBlobUrls();
    };
  }, []);
  
  const handleAddTag = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTag.trim()) {
      onAddTag([newTag.trim()]);
      setNewTag('');
    }
  };

  const handleCopyPublicUrl = async () => {
    if (!publicUrl) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(publicUrl);
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = publicUrl;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };
  
  if (!asset) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 p-8 rounded-lg">
        <p className="text-gray-500 text-center">Select an asset to view details</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow p-6 h-full flex flex-col">
      {/* Header with actions */}
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-medium text-gray-900">Asset Details</h3>
        <div className="flex space-x-2">
          <>
            <button
              onClick={onStartEditing}
              className="inline-flex items-center p-1 border border-transparent rounded-full text-blue-600 hover:bg-blue-50 focus:outline-none"
              aria-label="Edit asset"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => onDeleteAsset(asset.id)}
              className="inline-flex items-center p-1 border border-transparent rounded-full text-red-600 hover:bg-red-50 focus:outline-none"
              aria-label="Delete asset"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </>
        </div>
      </div>
      
      {/* Asset preview */}
      <div className="mb-6">
        <div 
          className="flex justify-center cursor-pointer group relative"
          onClick={onPreview}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onPreview?.();
            }
          }}
        >
          {asset.contentType === 'image' && mediaUrl ? (
            <div className="relative h-48 w-full max-w-md">
              <img
                key={mediaUrl}
                src={mediaUrl}
                alt={asset.name}
                className="object-contain w-full h-full transition-opacity group-hover:opacity-75"
              />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-30 rounded">
                <span className="text-white text-sm font-medium px-3 py-1 bg-black bg-opacity-50 rounded">Click to preview</span>
              </div>
            </div>
          ) : asset.contentType === 'video' && mediaUrl ? (
            <div className="relative h-48 w-full max-w-md">
              <video
                key={mediaUrl}
                src={mediaUrl}
                controls
                className="object-contain w-full h-full"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-30 rounded pointer-events-none">
                <span className="text-white text-sm font-medium px-3 py-1 bg-black bg-opacity-50 rounded">Click to preview</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 w-full bg-gray-100 rounded">
              {getAssetIcon(asset.contentType)}
            </div>
          )}
        </div>
        {(asset.contentType === 'image' || asset.contentType === 'video') && (
          <p className="text-center text-xs text-gray-500 mt-2">Click to preview in full screen</p>
        )}
      </div>
      
      {/* Asset details */}
      <div className="space-y-4 flex-1 overflow-y-auto">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <p className="text-gray-900">{asset.name}</p>
        </div>
        
        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <p className="text-gray-600">{asset.description || 'No description provided'}</p>
        </div>
        
        {/* Metadata */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <p className="text-sm text-gray-900 capitalize">{asset.contentType}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
            <p className="text-sm text-gray-900">{asset.size}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Uploaded By</label>
            <p className="text-sm text-gray-900">{asset.uploadedBy}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Upload Date</label>
            <p className="text-sm text-gray-900">{asset.uploadDate}</p>
          </div>
        </div>
        
        {/* URL */}
        {(asset.url || (asset.sourceType === 'url' && asset.originalAsset?.meta?.url)) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
            <div className="flex items-center">
              <LinkIcon className="h-4 w-4 text-gray-400 mr-1" />
              <a 
                href={asset.sourceType === 'url' && asset.originalAsset?.meta?.url ? asset.originalAsset.meta.url : asset.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 truncate"
              >
                {asset.sourceType === 'url' && asset.originalAsset?.meta?.url ? asset.originalAsset.meta.url : asset.url}
              </a>
            </div>
          </div>
        )}
        
        {/* Public URL */}
        {publicUrl && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Public URL</label>
            <div className="flex items-center space-x-2">
              <div className="flex-1 flex items-center bg-gray-50 rounded-md px-3 py-2">
                <LinkIcon className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-900 truncate">
                  {publicUrl}
                </span>
              </div>
              <button
                onClick={handleCopyPublicUrl}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {copySuccess ? (
                  <>
                    <CheckIcon className="h-4 w-4 mr-1 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <ClipboardIcon className="h-4 w-4 mr-1" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This URL can be used to access the asset publicly without authentication.
            </p>
          </div>
        )}
        
        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
            <TagIcon className="h-4 w-4 text-gray-400 mr-1" />
            Tags
          </label>
          
          <div className="flex flex-wrap gap-2 mb-2">
            {asset.tags && asset.tags.length > 0 ? (
              asset.tags.map((tag) => (
                <span 
                  key={tag.id} 
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {tag.name}
                  <button
                    type="button"
                    onClick={() => onRemoveTag(tag.name)}
                    className="ml-1 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:text-blue-600 focus:outline-none"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              ))
            ) : (
              <span className="text-sm text-gray-500">No tags</span>
            )}
          </div>
          
          <div className="mt-4">
            <div className="flex items-end gap-2">
              <div className="flex-grow">
                <label htmlFor="new-tags-input" className="block text-sm font-medium text-gray-700 mb-1">Add new tags</label>
                <TaggedSelect
                  id="new-tags-input"
                  placeholder="Select existing tags or type to create new ones..."
                  options={availableTags.filter(tag => 
                    !asset.tags?.some(assetTag => assetTag.name === tag.value)
                  )}
                  value={tagsToAdd.map(t => t.value)}
                  onChange={(values) => {
                    const newTags = (values as string[]).map(v => ({ value: v, label: v }));
                    setTagsToAdd(newTags);
                  }}
                  multiple
                  editable
                  onCreateOption={(label) => {
                    // Add the new tag to the selection
                    const newTag = { value: label, label };
                    setTagsToAdd(prev => [...prev, newTag]);
                  }}
                />
              </div>
              <button
                onClick={() => {
                  if (tagsToAdd.length > 0) {
                    onAddTag(tagsToAdd.map(t => t.value));
                    setTagsToAdd([]);
                  }
                }}
                disabled={tagsToAdd.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Add Tags
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
